import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import type { ComponentProps, ReactNode } from 'react';
import { LAYER } from '../lib/layer.ts';
import { cx } from '../lib/styled.tsx';

export interface DialogProps extends ComponentProps<typeof BaseDialog.Root> {
  trigger?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
  /**
   * How wide, when the default 28rem is not enough — a dialog full of cards
   * rather than of text.
   *
   * A prop rather than a width in `className`, because a width passed there
   * does not reliably win: both classes are one utility of the same
   * specificity, and which applies is decided by the order Tailwind happens to
   * emit them in. A dialog that silently kept its default width is how five
   * cards ended up in a single column, taller than the window and with nothing
   * to scroll.
   */
  width?: number;
}

/**
 * Opinionated shell over Base UI Dialog: backdrop + centred popup + enter/exit
 * transitions driven by the data-*-style attributes Base UI sets.
 */
export function Dialog({
  trigger,
  title,
  description,
  children,
  className,
  width,
  ...rootProps
}: DialogProps) {
  return (
    <BaseDialog.Root {...rootProps}>
      {trigger ? <BaseDialog.Trigger render={trigger as never} /> : null}
      <BaseDialog.Portal>
        <BaseDialog.Backdrop
          className={cx(
            `${LAYER.dialog} fixed inset-0 bg-scrim backdrop-blur-[4px] transition-opacity duration-200`,
            'data-starting-style:opacity-0 data-ending-style:opacity-0',
          )}
        />
        <BaseDialog.Popup
          className={cx(
            `${LAYER.dialog} fixed top-1/2 left-1/2 -translate-1/2`,
            width === undefined ? 'w-[min(28rem,calc(100vw-2rem))]' : '',
            // A dialog taller than the window used to simply run off it, with
            // no way to reach the bottom. It now stops at the window and
            // scrolls inside itself.
            'max-h-[calc(100dvh-4rem)] overflow-y-auto',
            'rounded-modal bg-surface-raised px-8 py-6 text-ink shadow-modal',
            'transition-[opacity,scale] duration-200 ease-out-quint outline-none',
            'data-starting-style:scale-95 data-starting-style:opacity-0',
            'data-ending-style:scale-95 data-ending-style:opacity-0',
            className,
          )}
          style={width === undefined ? undefined : { width: `min(${width}px, calc(100vw - 2rem))` }}
        >
          <BaseDialog.Title className="font-display text-xl font-bold tracking-tight">
            {title}
          </BaseDialog.Title>
          {description ? (
            <BaseDialog.Description className="mt-2 text-bodysm leading-relaxed text-ink-muted">
              {description}
            </BaseDialog.Description>
          ) : null}
          <div className="mt-5">{children}</div>
        </BaseDialog.Popup>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}

export const DialogClose = BaseDialog.Close;
